import { useContext, useReducer, useRef } from 'react';
import {
	GET_AVAILABLE_PROCESSES,
	OCCUPANCY_LOADED,
	OCCUPANCY_UPDATED,
	PROCESS_UPDATE,
	REQUEST_ERROR,
	RESET,
	RESET_IF_ACTIVE_PROCESS_MATCHES,
	SET_REPORT_DATA,
	SET_SEEDTYPE,
	SET_SEEDTYPEID_FOR_PROCESSINFORMATION,
	SET_SELECTION,
	START_PROCESS,
	UPDATE_DISPLAY_PREFIX,
	UPDATE_DISPLAY_VALUE,
	SET_TASK_DATA,
} from '../types/contextTypes';
import { MULTIHARVEST, REPRINT } from '../types/processControlTypes';
import { HttpAgent } from '../../utility/HttpAgent';
import UIContext from '../ui/UIContext';
import ProcessContext from './ProcessContext';
import ProcessReducer from './ProcessReducer';
import AuthContext from '../auth/AuthContext';
import ProcessesApiService from '../../services/apiServices/ProcessesApiService';
import { HELP_DESK_LINK } from '../../constants/helpDesk';
import EntitiesApiService from '../../services/apiServices/EntitiesApiService';

const ProcessState = (props) => {
	const initialState = {
		processes: [],
		backIsAllowed: false,
		activeProcessId: null,
		processError: null,
		selection: null,
		reportData: null,
		additionalProcessInformation: null,
		requestedInformationType: null,
		processFinished: false,
		processStarted: false,
		activeProcessType: null,
		selectedDestinationSlot: null,
		destinationSlotType: 'TraySlot',
		destinationEntityNumRows: 2,
		destinationEntityNumCols: 8,
		occupancyArray: [],
		taskData: null,
		numPadDisplay: {
			prefix: null,
			value: '',
			combine: function () {
				return this.prefix + this.value;
			},
		},
		informationTypes: {
			EntityName: 0,
			Text: 1,
			Integer: 2,
			Photo: 3,
			Weight: 4,
			SeedSlotSelection: 5,
			PlantSlotSelection: 6,
			Confirm: 7,
			TraySlot: 8,
			PanelSlot: 9,
			Option: 10,
		},
		processTypes: {
			Seeding: 0,
			Transplanting: 1,
			Repositioning: 2,
			Harvesting: 3,
			Weighing: 4,
			EndOfLife: 5,
			Discarding: 6,
			Reporting: 7,
			Photographing: 8,
		},
	};
	const [state, dispatch] = useReducer(ProcessReducer, initialState);
	const finishProcessRequestsRef = useRef(new Map());
	const deletedProcessIdsRef = useRef(new Set());
	const { setInfo, setLoading, addNotification, addRedInfoNotification } = useContext(UIContext);
	const { currentContainer, currentTenant } = useContext(AuthContext);

	const notifySuccess = (message) => {
		addNotification(message, 'success');
	};

	const notifyWarning = (message) => {
		addNotification(message, 'error');
	};

	const notifyError = (message, errorBoundary = null) => {
		addNotification(message, 'error-stay', errorBoundary);
	};

	const notifyInfo = (message) => {
		addNotification(message, 'info');
	};

	const notifyStayInformation = (message) => {
		addNotification(message, 'info-stay');
	};

	const notifyInfoRed = (message) => {
		addRedInfoNotification(message, 'info-red');
	};

	const updateDisplayPrefix = (prefix) => {
		dispatch({
			type: UPDATE_DISPLAY_PREFIX,
			payload: prefix,
		});
	};

	const updateDisplayValue = (newDisplayValue) => {
		dispatch({
			type: UPDATE_DISPLAY_VALUE,
			payload: newDisplayValue,
		});
	};

	const finishProcess = async () => {
		const processIdToKill = state.activeProcessId;
		if (!processIdToKill) {
			return;
		}

		if (deletedProcessIdsRef.current.has(processIdToKill)) {
			return;
		}

		const ongoingRequest = finishProcessRequestsRef.current.get(processIdToKill);
		if (ongoingRequest) {
			await ongoingRequest;
			return;
		}

		const deleteRequest = (async () => {
			let deleteSucceeded = false;
			try {
				setLoading(true);
				await ProcessesApiService.deleteProcess(currentTenant?.id, currentContainer?.id, processIdToKill);
				deleteSucceeded = true;
				deletedProcessIdsRef.current.add(processIdToKill);
			} catch (error) {
				const htmlStatusCode = error?.response?.status;
				const innerStatusCode = error?.response?.data?.statusCode;
				const processAlreadyDeleted = innerStatusCode === 404 && htmlStatusCode === 400;

				if (processAlreadyDeleted) {
					deleteSucceeded = true;
					deletedProcessIdsRef.current.add(processIdToKill);
				} else {
					handleError(error);
				}
			} finally {
				if (deleteSucceeded) {
					resetProcessStateIfActiveProcessMatches(processIdToKill);
				}
				finishProcessRequestsRef.current.delete(processIdToKill);
				setLoading(false);
			}
		})();

		finishProcessRequestsRef.current.set(processIdToKill, deleteRequest);
		await deleteRequest;
	};

	const modifyProcess = async (action, value) => {
		let url = `api/v1/tenants/${currentTenant?.id}/Containers/${currentContainer?.id}/ProcprocessControl/${state.activeProcessId}`;
		let body = {
			value,
			action,
		};

		try {
			setLoading(true);
			var header = { headers: { 'X-Correlation-Id': state.activeProcessId } };
			let result = await HttpAgent.put(url, JSON.stringify(body), header);

			let additionalInfo = null;
			if (result.data.data?.availableInformation) {
				additionalInfo = result.data.data?.availableInformation;
			}
			if (result?.data?.processFinished) {
				notifySuccess('Process successfully finished');
				state.processFinished = true;
			} else {
				if (
					(state.activeProcessType == 3 && state.requestedInformationType == 13) ||
					(state.activeProcessType == 3 &&
						state.requestedInformationType == 14 &&
						additionalInfo.plantSlotOrdinalNumber == state.additionalProcessInformation.plantSlotOrdinalNumber)
				) {
					// notifySuccess('Label printed');
				}

				if (
					state.activeProcessType == 3 &&
					state.requestedInformationType == 14 &&
					additionalInfo.plantSlotOrdinalNumber != state.additionalProcessInformation.plantSlotOrdinalNumber
				) {
					notifySuccess('Plant harvested');
				}
			}

			setInfo(result.data?.requestedInformation?.text);

			dispatch({
				type: PROCESS_UPDATE,
				payload: {
					activeProcessType: result.data?.type,
					requestedInformationType: result.data?.requestedInformation?.type,
					processInfoMessage: result.data?.requestedInformation?.text,
					processFinished: result.data?.processFinished,
					additionalProcessInformation: additionalInfo,
					backIsAllowed: result.data.data?.backIsAllowed,
				},
			});
		} catch (error) {
			handleError(error);
		} finally {
			setLoading(false);
		}
	};

	const continueProcess = async (processId, value) => {
		let body = {};
		if (value !== undefined) {
			body = {
				type: state.requestedInformationType,
				value,
			};
		}

		try {
			setLoading(true);
			var header = { headers: { 'X-Correlation-Id': processId } };
			// const result = await HttpAgent.put(url, JSON.stringify(body), header);
			const result = await ProcessesApiService.continueProcess(currentTenant?.id, currentContainer?.id, processId, body, header);
			if (result.data?.status !== 'success') {
				throw new Error(result.data.message);
			}

			let additionalInfo = null;
			if (result.data.data?.availableInformation) {
				additionalInfo = result.data.data?.availableInformation;
			}
			if (result?.data?.data?.processFinished) {
				notifySuccess('Process successfully finished');
				state.processFinished = true;
			} else {
				if (
					(state.activeProcessType == 3 && state.requestedInformationType == 13) ||
					(state.activeProcessType == 3 &&
						state.requestedInformationType == 14 &&
						additionalInfo.plantSlotOrdinalNumber == state.additionalProcessInformation.plantSlotOrdinalNumber)
				) {
					// notifySuccess('Label printed');
				}

				if (
					state.activeProcessType == 3 &&
					state.requestedInformationType == 14 &&
					additionalInfo.plantSlotOrdinalNumber != state.additionalProcessInformation.plantSlotOrdinalNumber &&
					state.additionalProcessInformation.plantSlotOrdinalNumber != state.additionalProcessInformation.firstPlantHarvested
				) {
					notifySuccess('Plant harvested');
				}
			}
			setInfo(result.data.data?.requestedInformation?.text);
			dispatch({
				type: PROCESS_UPDATE,
				payload: {
					activeProcessType: result.data.data?.type,
					requestedInformationType: result.data.data?.requestedInformation?.type,
					processInfoMessage: result.data.data?.requestedInformation?.text,
					processFinished: result.data.data?.processFinished,
					additionalProcessInformation: additionalInfo,
					backIsAllowed: result.data.data?.backIsAllowed,
				},
			});
			return state.processFinished;
		} catch (error) {
			console.error('ERROR IN CONTINUE PROCESS: ', error);
			await handleError(error, <>We have encountered a problem while continuing the Process. Please contact the support team {HELP_DESK_LINK}.</>);
			throw error;
		} finally {
			setLoading(false);
		}
	};

	const revertProcess = async (processId) => {
		try {
			setLoading(true);
			var header = { headers: { 'X-Correlation-Id': processId } };
			// const result = await HttpAgent.put(url, JSON.stringify(body), header);
			const result = await ProcessesApiService.revertProcess(currentTenant?.id, currentContainer?.id, processId, header);
			if (result.data?.status !== 'success') {
				throw new Error(result.data.message);
			}

			let additionalInfo = null;
			if (result.data.data?.availableInformation) {
				additionalInfo = result.data.data?.availableInformation;
			}
			if (result?.data?.data?.processFinished) {
				notifySuccess('Process successfully finished');
				state.processFinished = true;
			} else {
				if (
					(state.activeProcessType == 3 && state.requestedInformationType == 13) ||
					(state.activeProcessType == 3 &&
						state.requestedInformationType == 14 &&
						additionalInfo.plantSlotOrdinalNumber == state.additionalProcessInformation.plantSlotOrdinalNumber)
				) {
					// notifySuccess('Label printed');
				}

				if (
					state.activeProcessType == 3 &&
					state.requestedInformationType == 14 &&
					additionalInfo.plantSlotOrdinalNumber != state.additionalProcessInformation.plantSlotOrdinalNumber &&
					state.additionalProcessInformation.plantSlotOrdinalNumber != state.additionalProcessInformation.firstPlantHarvested
				) {
					notifySuccess('Plant harvested');
				}
			}

			setInfo(result.data.data?.requestedInformation?.text);

			dispatch({
				type: PROCESS_UPDATE,
				payload: {
					activeProcessType: result.data.data?.type,
					requestedInformationType: result.data.data?.requestedInformation?.type,
					processInfoMessage: result.data.data?.requestedInformation?.text,
					processFinished: result.data.data?.processFinished,
					additionalProcessInformation: additionalInfo,
					backIsAllowed: result.data.data?.backIsAllowed,
				},
			});
		} catch (error) {
			console.error('ERROR IN REVERT PROCESS: ', error);
			await handleError(error?.message);
			throw new Error(error);
		} finally {
			setLoading(false);
		}
	};

	const controlProcess = async (processId, action, showMessage, successMessage, weightInfo) => {
		try {
			let body;
			setLoading(true);
			if (action === REPRINT || action === MULTIHARVEST) {
				body = JSON.stringify({
					action: action,
					value: weightInfo,
				});
			} else {
				body = JSON.stringify({
					action: action,
				});
			}

			let result = await HttpAgent.put(`api/v1/tenants/${currentTenant?.id}/Containers/${currentContainer?.id}/Processes/${processId}/modify`, body);

			if (showMessage) {
				notifySuccess(successMessage, 'success');
			}

			updateProcessState(result);
		} catch (err) {
			console.error(err);
			notifyWarning(err.message);
		} finally {
			setLoading(false);
		}
	};

	const updateProcessState = (result) => {
		if (Object.prototype.hasOwnProperty.call(result, 'data')) {
			dispatch({
				type: PROCESS_UPDATE,
				payload: {
					activeProcessType: result.data?.type,
					requestedInformationType: result.data?.requestedInformation?.type,
					processInfoMessage: result.data?.requestedInformation?.text,
					processFinished: result.data?.processFinished,
					additionalProcessInformation: result.data?.availableInformation,
					backIsAllowed: result.data?.backIsAllowed,
				},
			});
		} else {
			dispatch({
				type: PROCESS_UPDATE,
				payload: {
					activeProcessType: result?.type,
					requestedInformationType: result?.requestedInformation?.type,
					processInfoMessage: result?.requestedInformation?.text,
					processFinished: result?.processFinished,
					additionalProcessInformation: result?.availableInformation,
					backIsAllowed: result?.backIsAllowed,
				},
			});
		}
	};

	// Create a new function to process panel occupancy
	// Everything else is the same
	const loadOccupancy = async (sourceEntityName) => {
		setLoading(true);
		updateOccupancy([]);
		let loadedArray = [];
		try {
			const result = await EntitiesApiService.getEntityOccupancy(currentTenant?.id, currentContainer?.id, sourceEntityName);
			const isTrayType = () => {
				const name = sourceEntityName.toLowerCase();
				return name.startsWith('t') && !name.startsWith('tbl');
			};

			loadedArray = isTrayType()
				? result.data.data.occupancy.map((item) => ({
					index: item.ordinalNumber,
					isAvailable: item.occupancy === 'available',
					isSelected: false,
					display: item.occupancy,
					group: item.groupCounter,
					slotSpan: item.slotSpan,
					isMultiharvestable: item.isMultiharvestable,
				}))
				: [...result.data.data.occupancy];

			dispatch({
				type: OCCUPANCY_LOADED,
				payload: loadedArray,
			});
		} catch (error) {
			handleError(error);
		} finally {
			setLoading(false);
		}
		return loadedArray;
	};

	const updateOccupancy = (updatedOccupancyArray) => {
		dispatch({
			type: OCCUPANCY_UPDATED,
			payload: updatedOccupancyArray,
		});
	};

	const setSelectedSeedType = (seedName) => {
		dispatch({
			type: SET_SEEDTYPE,
			payload: seedName,
		});
	};

	const setSelectedSeedTypeIdForProcessInformation = (seedTypeId) => {
		dispatch({
			type: SET_SEEDTYPEID_FOR_PROCESSINFORMATION,
			payload: seedTypeId,
		});
	};

	const getAvailableProcesses = async () => {
		try {
			if (!currentContainer?.id) {
				return;
			}

			setLoading(true);

			const result = await HttpAgent.get(`api/v1/tenants/${currentTenant?.id}/containers/${currentContainer?.id}/processes`);

			dispatch({
				type: GET_AVAILABLE_PROCESSES,
				payload: {
					result,
				},
			});
		} catch (error) {
			handleError(error);
		} finally {
			setLoading(false);
		}
	};

	const startProcess = async (type, taskId = null) => {
		if (type == null || type < 0) {
			throw Error('A process type must be provided.');
		}
		try {
			setLoading(true);

			const rawResult = await ProcessesApiService.startProcess(currentTenant.id, currentContainer?.id, type, taskId);
			const result = rawResult.data;
			if (result.status !== 'success') {
				dispatch({
					type: REQUEST_ERROR,
					payload: result,
				});
				console.error(result);
			} else {
				dispatch({
					type: START_PROCESS,
					payload: {
						id: result.data?.id,
						type,
					},
				});
				await continueProcess(result.data.id);
			}
		} catch (error) {
			handleError(error);
			throw error;
		} finally {
			setLoading(false);
		}
	};
	const splitUpArray = (array, limit) => {
		let newArray = [];
		let temp = [];
		let counter = 0;
		for (let i = 0; i <= array.length; i++) {
			temp.push(array[i]);

			if (counter === limit - 1) {
				newArray.push(temp);
				// if one channel is full, temp array (which is a column) gets pushed to the return array
				temp = [];
				counter = 0;
			} else {
				counter += 1;
				// if the counter exceeds 500 slots - the panel is full and stop working on it
				if (i === array.length - 1) {
					newArray.push(temp);
					break;
				}
			}
		}

		return newArray;
	};

	const isNetworkError = (error) => {
		if (error.code === 'ERR_NETWORK') return true;

		if (error.code === 'ECONNABORTED') return true;

		if (typeof error.message === 'string' && error.message.toLowerCase().includes('network error')) {
			return true;
		}
		return false;
	};
	const handleError = async (error, overrideMessage = null) => {
		console.error('ProcessState Error:', error);

		if (isNetworkError(error)) {
			notifyError('Network Error.');
		} else if (overrideMessage) {
			notifyError(overrideMessage);
		} else {
			const serverMsg = error.response?.data?.message || error.message;
			notifyError(serverMsg);
		}
		dispatch({
			type: REQUEST_ERROR,
			payload: error,
		});
	};
	// const handleError = async (error) => {
	//   console.error(error);
	//   dispatch({
	//     type: REQUEST_ERROR,
	//     payload: error,
	//   });
	// };

	const resetProcessState = () => {
		dispatch({
			type: RESET,
			payload: { ...initialState, processes: state.processes },
		});
	};

	const resetProcessStateIfActiveProcessMatches = (processId) => {
		dispatch({
			type: RESET_IF_ACTIVE_PROCESS_MATCHES,
			payload: {
				processId,
				nextState: { ...initialState, processes: state.processes },
			},
		});
	};

	const setSelection = (selection) => {
		dispatch({
			type: SET_SELECTION,
			payload: selection,
		});
	};

	const setReportData = (reportData) => {
		dispatch({
			type: SET_REPORT_DATA,
			payload: reportData,
		});
	};

	const setTaskData = (taskData) => {
		dispatch({
			type: SET_TASK_DATA,
			payload: taskData,
		});
	};

	return (
		<ProcessContext.Provider
			value={{
				...state,
				finishProcess,
				getAvailableProcesses,
				loadOccupancy,
				continueProcess,
				handleError,
				revertProcess,
				controlProcess,
				startProcess,
				modifyProcess,
				setReportData,
				setSelection,
				resetProcessState,
				updateOccupancy,
				splitUpArray,
				setSelectedSeedType,
				updateDisplayPrefix,
				updateDisplayValue,
				updateProcessState,
				notifySuccess,
				notifyWarning,
				notifyInfo,
				notifyStayInformation,
				notifyInfoRed,
				notifyError,
				setTaskData,
				setSelectedSeedTypeIdForProcessInformation,
			}}>
			{props.children}
		</ProcessContext.Provider>
	);
};

export default ProcessState;
